/**
 * VersionAccordion - Single version entry in the Version History Modal
 * Features: HTML5 details accordion, auto-expand for first unread, markdown support
 */

import React from 'react';
import { ChevronDown } from 'lucide-react';

export interface VersionAccordionProps {
  version: string;
  date: string;
  title: string;
  breaking?: boolean;
  important?: boolean;
  highlights?: string[];
  content: string;
  isUnread: boolean;
  isLatest: boolean;
  isFirstUnread?: boolean;
}

/**
 * Format date string to readable format
 * Example: "2025-01-10" -> "January 10, 2025"
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Simple markdown parser for version content
 * Supports: headings, bold, lists, code, links, horizontal rules
 */
function parseMarkdown(markdown: string): React.ReactElement[] {
  const lines = markdown.split('\n');
  const elements: React.ReactElement[] = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;
    const trimmed = line.trim();

    // Skip empty lines
    if (!trimmed) {
      elements.push(<div key={key++} className="h-3" />);
      continue;
    }

    // Horizontal rule
    if (trimmed === '---') {
      elements.push(
        <hr key={key++} className="border-zinc-700 my-8 opacity-50" />
      );
      continue;
    }

    // Headings
    if (trimmed.startsWith('# ')) {
      elements.push(
        <h1 key={key++} className="text-3xl font-bold text-white mb-4 mt-6 first:mt-0">
          {trimmed.slice(2)}
        </h1>
      );
      continue;
    }
    if (trimmed.startsWith('## ')) {
      elements.push(
        <h2 key={key++} className="text-2xl font-semibold text-emerald-400 mb-3 mt-6">
          {trimmed.slice(3)}
        </h2>
      );
      continue;
    }
    if (trimmed.startsWith('### ')) {
      elements.push(
        <h3 key={key++} className="text-xl font-semibold text-zinc-200 mb-2 mt-4">
          {trimmed.slice(4)}
        </h3>
      );
      continue;
    }

    // Unordered list
    if (trimmed.startsWith('- ')) {
      elements.push(
        <li key={key++} className="text-zinc-300 ml-4 mb-1 list-disc list-inside">
          {parseInlineMarkdown(trimmed.slice(2))}
        </li>
      );
      continue;
    }

    // Regular paragraph
    elements.push(
      <p key={key++} className="text-zinc-300 mb-3 leading-relaxed">
        {parseInlineMarkdown(trimmed)}
      </p>
    );
  }

  return elements;
}

/**
 * Parse inline markdown (bold, code, links)
 */
function parseInlineMarkdown(text: string): (string | React.ReactElement)[] {
  const parts: (string | React.ReactElement)[] = [];
  let remaining = text;
  let key = 0;

  while (remaining) {
    // Bold **text**
    const boldMatch = remaining.match(/\*\*(.*?)\*\*/);
    if (boldMatch && boldMatch.index !== undefined) {
      if (boldMatch.index > 0) {
        parts.push(remaining.slice(0, boldMatch.index));
      }
      parts.push(
        <strong key={key++} className="font-semibold text-white">
          {boldMatch[1]}
        </strong>
      );
      remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
      continue;
    }

    // Code `text`
    const codeMatch = remaining.match(/`(.*?)`/);
    if (codeMatch && codeMatch.index !== undefined) {
      if (codeMatch.index > 0) {
        parts.push(remaining.slice(0, codeMatch.index));
      }
      parts.push(
        <code
          key={key++}
          className="px-1.5 py-0.5 bg-zinc-800 text-emerald-400 rounded font-mono text-sm"
        >
          {codeMatch[1]}
        </code>
      );
      remaining = remaining.slice(codeMatch.index + codeMatch[0].length);
      continue;
    }

    // No more special formatting
    parts.push(remaining);
    break;
  }

  return parts;
}

export function VersionAccordion({
  version,
  date,
  title,
  breaking,
  important,
  highlights,
  content,
  isUnread,
  isFirstUnread,
}: VersionAccordionProps) {
  return (
    <details
      name="versions"
      className="border-b border-zinc-800 last:border-0 group"
      open={isFirstUnread}
    >
      <summary
        className="
          cursor-pointer hover:bg-zinc-800/50
          p-4 rounded-lg transition-colors
          flex items-center justify-between
          list-none [&::-webkit-details-marker]:hidden
        "
      >
        <div className="flex items-center gap-3 flex-1">
          {/* Unread indicator */}
          {isUnread && (
            <div
              className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"
              aria-label="Unread"
            />
          )}

          {/* Version + Date */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-white">
              v{version}
            </span>
            <span className="text-zinc-500 text-sm">
              {formatDate(date)}
            </span>
          </div>

          {/* Badges */}
          {breaking && (
            <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded font-medium">
              ⚠️ Breaking
            </span>
          )}
          {important && (
            <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded font-medium">
              ✨ Important
            </span>
          )}
        </div>

        {/* Chevron (rotates on open) */}
        <ChevronDown className="w-5 h-5 text-zinc-400 transition-transform group-open:rotate-180" />
      </summary>

      {/* Content (hidden when collapsed) */}
      <div className="px-4 pb-4 pt-2">
        {/* Title */}
        {title && (
          <h3 className="text-lg font-semibold text-white mb-3">
            {title}
          </h3>
        )}

        {/* Highlights */}
        {highlights && highlights.length > 0 && (
          <div className="mb-4">
            <ul className="space-y-1">
              {highlights.map((highlight, index) => (
                <li key={index} className="text-zinc-300 ml-4 list-disc list-inside">
                  {highlight}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Main content */}
        <div className="prose prose-invert prose-zinc max-w-none">
          {parseMarkdown(content)}
        </div>
      </div>
    </details>
  );
}
